import React from 'react';
import { documentToReactComponents, type Options } from '@contentful/rich-text-react-renderer';
import {
  BLOCKS,
  INLINES,
  type Document,
  type Block,
  type Inline,
  type Text,
} from '@contentful/rich-text-types';
import type { RichTextProps } from './types';
import Image from 'next/image';
import { UnorderedList, OrderedList, ListItem } from './Lists';

function renderEntry(node: Block | Inline) {
  if (node.data.target.sys.contentType.sys.id === 'videoEmbed') {
    return (
      <iframe
        src={node.data.target.fields.embedUrl}
        height='100%'
        width='100%'
        frameBorder='0'
        scrolling='no'
        loading='lazy'
        title={node.data.target.fields.title}
        allowFullScreen={true}
      />
    );
  }

  return null;
}

function renderAsset(node: Block | Inline) {
  let url = node.data.target.fields.file.url;
  if (url.startsWith('//')) url = `https:${url}`;

  return (
    <Image
      src={url}
      height={node.data.target.fields.file.details.image.height}
      width={node.data.target.fields.file.details.image.width}
      sizes='(max-width: 768px) 100vw, 768px'
      alt={node.data.target.fields.description}
    />
  );
}

function isEmptyChildren(children: React.ReactNode) {
  return React.Children.toArray(children).every((child) => {
    if (typeof child === 'string') return child.trim() === '';
    return child == null;
  });
}

function isEmptyTextNode(node: unknown): node is Text {
  return Boolean(
    node &&
    typeof node === 'object' &&
    'nodeType' in node &&
    (node as Text).nodeType === 'text' &&
    'value' in node,
  );
}

function isEmptyParagraphNode(node: Block | Inline) {
  if (node.nodeType !== BLOCKS.PARAGRAPH) return false;

  return (node.content || []).every((child) => {
    if (isEmptyTextNode(child)) {
      return child.value.trim() === '';
    }

    return false;
  });
}

function trimSingleTrailingEmptyParagraph(document: Document): Document {
  const content = [...document.content];
  const lastNode = content[content.length - 1] as Block | Inline | undefined;

  if (lastNode && isEmptyParagraphNode(lastNode)) {
    content.pop();
  }

  return {
    ...document,
    content,
  };
}

const baseOptions: Options = {
  renderNode: {
    [BLOCKS.EMBEDDED_ENTRY]: renderEntry,
    [BLOCKS.EMBEDDED_RESOURCE]: renderEntry,
    [BLOCKS.EMBEDDED_ASSET]: renderAsset,
    // Newlines in Contentful's rich text are not represented as separate blocks, so we handle them here.
    [BLOCKS.PARAGRAPH]: (_node, children) => (
      <p className='copy-large text-inherit'>{isEmptyChildren(children) ? <br /> : children}</p>
    ),
    [BLOCKS.HEADING_1]: (_node, children) => <h1 className='heading-1 text-inherit'>{children}</h1>,
    [BLOCKS.HEADING_2]: (_node, children) => <h2 className='heading-2 text-inherit'>{children}</h2>,
    [BLOCKS.HEADING_3]: (_node, children) => <h3 className='heading-3 text-inherit'>{children}</h3>,
    [BLOCKS.HEADING_4]: (_node, children) => <h4 className='heading-4 text-inherit'>{children}</h4>,
    [BLOCKS.HEADING_5]: (_node, children) => <h5 className='heading-5 text-inherit'>{children}</h5>,
    [BLOCKS.HEADING_6]: (_node, children) => <h6 className='heading-6 text-inherit'>{children}</h6>,
    [BLOCKS.UL_LIST]: (_node, children) => <UnorderedList>{children}</UnorderedList>,
    [BLOCKS.OL_LIST]: (_node, children) => <OrderedList>{children}</OrderedList>,
    [BLOCKS.LIST_ITEM]: (_node, children) => <ListItem>{children}</ListItem>,
    [INLINES.HYPERLINK]: (node, children) => (
      <a
        href={node.data.uri}
        className='underline underline-offset-2 hover:opacity-80'
      >
        {children}
      </a>
    ),
  },
  // This handles newlines in text nodes, which Contentful doesn't represent as separate blocks.
  // We split on newlines and rejoin with <br /> elements, which is a common approach for handling this.
  renderText: (text) =>
    text
      .split('\n')
      .flatMap((segment, index) => (index === 0 ? [segment] : [<br key={index} />, segment])),
};

export const RichText = ({ data }: { data: RichTextProps }) => {
  const content = data?.content;
  const className = data?.className || '';
  const options = data?.options;

  if (!content) return null;

  const normalizedContent = trimSingleTrailingEmptyParagraph(content);

  const mergedOptions: Options = {
    ...baseOptions,
    ...options,
    renderNode: {
      ...baseOptions.renderNode,
      ...options?.renderNode,
    },
    renderMark: {
      ...baseOptions.renderMark,
      ...options?.renderMark,
    },
    renderText: options?.renderText ?? baseOptions.renderText,
  };

  return (
    <div className={`rich-text ${className}`.trim()}>
      {documentToReactComponents(normalizedContent, mergedOptions)}
    </div>
  );
};
